import { useContext } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { t } from '@lingui/core/macro';
import { useOptionalBookmarkSelection } from '../../../../contexts/BookmarkSelectionContext';
import { useBookmarkPageContent } from '../../../../contexts/BookmarkPageContext';
import { UserPreferencesContext } from '../../../../contexts/UserPreferencesContext';
import { useReadUrl } from '../../../../hooks/useReadUrl';
import { requestReaderFocus } from '../../../../services/readerFocusIntent';
import { useCurrentUser } from '../../../../services/userHooks';
import { isMobileDevice } from '../../../../utils/platformUtils';
import {
  isInteractiveElement,
  shouldHandleKeyboardSelection,
} from '../../../../utils/interactiveUtils';

interface UseBookmarkInteractionsProps {
  bookmarkId: string;
  bookmarkTitle: string;
  bookmarkUrl: string;
}

export function useBookmarkInteractions({
  bookmarkId,
  bookmarkTitle,
  bookmarkUrl,
}: UseBookmarkInteractionsProps) {
  const selectionContext = useOptionalBookmarkSelection();
  const bookmarkPage = useBookmarkPageContent();
  const navigate = useNavigate();
  const { to, params, search } = useReadUrl(bookmarkId);
  const { currentUser } = useCurrentUser();
  // Read directly from context so callers without UserPreferencesProvider
  // (e.g. logged-out / drag-drop tests) don't crash. Double-click is a
  // logged-in-only action anyway.
  const userPreferences = useContext(UserPreferencesContext);
  const setReaderLayoutMode = userPreferences?.setReaderLayoutMode;
  const isLoggedIn = !!currentUser;
  const isMobile = isMobileDevice();

  const orderedIds = bookmarkPage?.bookmarks.map((b) => String(b.id)) ?? [];

  const isSelected =
    selectionContext?.selectedBookmarkIds.has(String(bookmarkId)) || false;
  const isBatchMode = selectionContext?.isBatchMode || false;

  const showActions = isLoggedIn;
  const isKeyboardNavigable = isLoggedIn && (!isMobile || isBatchMode);
  const showAriaPressed = isLoggedIn && isBatchMode && !isMobile;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isLoggedIn) {
      return;
    }

    // In batch mode, row click toggles selection instead of navigating.
    // Skip the full interactive-element check so clicks on the title link
    // area (which fills the row width) still toggle selection.
    if (isBatchMode && selectionContext) {
      const target = event.target as Element;
      // Let bookmark actions handle their own clicks
      if (target.closest('.bookmark-actions')) {
        return;
      }
      event.preventDefault();
      if (event.shiftKey) {
        selectionContext.selectRange(String(bookmarkId), orderedIds);
      } else {
        selectionContext.toggleBookmark(String(bookmarkId));
      }
      return;
    }

    // Don't navigate if clicking an interactive element (like checkbox, links, buttons)
    if (isInteractiveElement(event.target as Element, event.currentTarget)) {
      return;
    }

    event.preventDefault();

    // Cmd+click enters batch mode and selects the bookmark
    // Also select the article currently open in the reader (3col)
    if ((event.ctrlKey || event.metaKey) && selectionContext && !isMobile) {
      const activeId = bookmarkPage?.activeBookmark?.id;
      if (activeId && String(activeId) !== String(bookmarkId)) {
        selectionContext.selectBookmark(String(activeId));
      }
      selectionContext.selectBookmark(String(bookmarkId));
      return;
    }

    // Shift+click selects from the active reader article to the clicked article
    if (event.shiftKey && selectionContext && !isMobile) {
      const activeId = bookmarkPage?.activeBookmark?.id;
      const anchor = activeId ? String(activeId) : undefined;
      selectionContext.selectRange(String(bookmarkId), orderedIds, anchor);
      return;
    }

    // Explicit open: move focus into the reader so screen-reader users land on
    // the article content instead of staying in the list.
    requestReaderFocus(String(bookmarkId));
    navigate({ to, params, search });
  };

  // Double-click on a row in 3-column view enters full-screen reader on the
  // double-clicked article, even if a different article is currently active.
  const handleDoubleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isLoggedIn || isBatchMode) return;
    if (bookmarkPage?.readerLayout !== '3col') return;
    if (isInteractiveElement(event.target as Element, event.currentTarget)) return;
    if (!setReaderLayoutMode) return;
    event.preventDefault();
    requestReaderFocus(String(bookmarkId));
    navigate({ to, params, search });
    setReaderLayoutMode('full');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!isLoggedIn) {
      return;
    }

    // ArrowUp/Down in batch mode: navigate or extend selection
    if (
      isBatchMode &&
      !isMobile &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp')
    ) {
      // In 3col with an active reader article, the global list listener owns
      // Shift+Arrow so the selection stays anchored at the active article and
      // can shrink cleanly. Letting it bubble avoids double-handling.
      if (
        event.shiftKey &&
        bookmarkPage?.readerLayout === '3col' &&
        bookmarkPage?.activeBookmark
      ) {
        return;
      }

      event.preventDefault();

      const currentIdx = orderedIds.indexOf(String(bookmarkId));
      const nextIdx = event.key === 'ArrowDown' ? currentIdx + 1 : currentIdx - 1;

      if (nextIdx >= 0 && nextIdx < orderedIds.length) {
        // Shift+arrow extends selection (from the last-selected row through
        // the new cursor — handles "plain-arrowed past rows, now Shift+Arrow
        // should grab everything in between"); plain arrow just moves focus.
        if (event.shiftKey && selectionContext) {
          const anchor = selectionContext.lastSelectedId ?? String(bookmarkId);
          selectionContext.selectRange(orderedIds[nextIdx], orderedIds, anchor);
        }
        const container = event.currentTarget.closest('section');
        const rows = container?.querySelectorAll<HTMLElement>('.bookmark-item');
        rows?.[nextIdx]?.focus();
      }

      return;
    }

    if (
      !shouldHandleKeyboardSelection(
        event.nativeEvent,
        event.target as Element,
        event.currentTarget,
      )
    ) {
      return;
    }

    if (isMobile) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    // In batch mode, Enter/Space toggles selection instead of navigating
    if (isBatchMode && selectionContext) {
      selectionContext.toggleBookmark(String(bookmarkId));
      return;
    }

    // Explicit open via keyboard: move focus into the reader content.
    requestReaderFocus(String(bookmarkId));
    navigate({ to, params, search });
  };

  const label = bookmarkTitle || bookmarkUrl;
  const ariaLabel = showAriaPressed
    ? isSelected
      ? t`Deselect article: ${label}`
      : t`Select article: ${label}`
    : undefined;

  return {
    isSelected,
    isBatchMode,
    isLoggedIn,
    isMobile,
    showActions,
    isKeyboardNavigable,
    showAriaPressed,
    handleClick,
    handleDoubleClick,
    handleKeyDown,
    ariaLabel,
    selectionContext,
  };
}
